import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../components/common/SafeIcon';
import Navbar from '../../components/nav/Navbar';
import LoadingScreen from '../../components/common/LoadingScreen';
import supabase from '../../supabase/supabase';
import { useAuth } from '../../context/AuthContext';
import logger from '../../lib/logger';

const {
  FiUsers, FiMusic, FiDollarSign, FiActivity,
  FiPlus, FiExternalLink, FiRefreshCw,
} = FiIcons;

const TABS = [
  { id: 'overview', label: 'Overview', icon: FiActivity   },
  { id: 'artists',  label: 'Artists',  icon: FiUsers      },
  { id: 'products', label: 'Products', icon: FiMusic      },
  { id: 'orders',   label: 'Orders',   icon: FiDollarSign },
];

const StatCard = ({ icon, label, value, accent = 'pink' }) => {
  const colors = { pink: 'text-brand-pink', coral: 'text-brand-coral', sky: 'text-sky-400', green: 'text-emerald-400' };
  return (
    <div className="p-6 bg-zinc-900 border border-zinc-800 rounded-[2rem] space-y-3">
      <div className={`w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center ${colors[accent]}`}>
        <SafeIcon icon={icon} className="text-lg" />
      </div>
      <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="text-3xl font-black text-white">{value}</p>
    </div>
  );
};

const OverviewTab = ({ stats }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    <StatCard icon={FiUsers}      label="Artists"     value={stats.artists}  accent="pink"  />
    <StatCard icon={FiMusic}      label="Drops"       value={stats.drops}    accent="coral" />
    <StatCard icon={FiDollarSign} label="Revenue"     value={stats.revenue}  accent="green" />
    <StatCard icon={FiActivity}   label="Active Subs" value={stats.subs}     accent="sky"   />
  </div>
);

const ArtistsTab = ({ artists }) => {
  const [search, setSearch] = useState('');
  const filtered = artists.filter(a =>
    a.name?.toLowerCase().includes(search.toLowerCase()) ||
    a.slug?.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search artists…"
          className="flex-1 px-5 py-3 bg-zinc-900 border border-zinc-800 rounded-2xl text-sm text-white placeholder-zinc-600 outline-none focus:border-brand-pink/40" />
        <button className="flex items-center gap-2 px-5 py-3 bg-white text-black font-black uppercase text-xs tracking-widest rounded-2xl hover:bg-zinc-200 transition-colors">
          <SafeIcon icon={FiPlus} /> Invite
        </button>
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded-[2rem] overflow-hidden">
        <table className="w-full text-left">
          <thead className="border-b border-zinc-800">
            <tr className="text-[10px] font-black tracking-widest text-zinc-500 uppercase">
              <th className="px-6 py-4">Artist</th>
              <th className="px-6 py-4">Genre</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/50">
            {filtered.length === 0 && (
              <tr><td colSpan={4} className="px-6 py-10 text-center text-zinc-600 text-sm font-bold">No artists in your label</td></tr>
            )}
            {filtered.map(a => (
              <tr key={a.id} className="hover:bg-zinc-800/30 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {a.avatar_url
                      ? <img src={a.avatar_url} alt={a.name} className="w-9 h-9 rounded-xl object-cover" />
                      : <div className="w-9 h-9 rounded-xl bg-zinc-800 flex items-center justify-center text-zinc-600 font-black text-sm">{a.name?.[0]}</div>
                    }
                    <div>
                      <p className="font-bold text-sm text-white">{a.name}</p>
                      <p className="text-[11px] text-zinc-500 font-mono">/{a.slug}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-zinc-400">{a.genre || '—'}</td>
                <td className="px-6 py-4">
                  <span className={`text-[10px] font-black uppercase tracking-widest ${
                    a.access_status === 'approved' ? 'text-emerald-400' : 'text-amber-400'
                  }`}>{a.access_status || 'pending'}</span>
                </td>
                <td className="px-6 py-4 text-right">
                  <a href={`/${a.slug}`} target="_blank" rel="noreferrer"
                    className="p-2 inline-flex hover:bg-zinc-800 rounded-lg text-zinc-400 hover:text-white transition-colors">
                    <SafeIcon icon={FiExternalLink} />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ProductsTab = ({ drops }) => {
  const [search, setSearch] = useState('');
  const filtered = drops.filter(d => d.title?.toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="space-y-4">
      <input value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search drops…"
        className="w-full px-5 py-3 bg-zinc-900 border border-zinc-800 rounded-2xl text-sm text-white placeholder-zinc-600 outline-none focus:border-brand-pink/40" />
      <div className="bg-zinc-900 border border-zinc-800 rounded-[2rem] overflow-hidden">
        <table className="w-full text-left">
          <thead className="border-b border-zinc-800">
            <tr className="text-[10px] font-black tracking-widest text-zinc-500 uppercase">
              <th className="px-6 py-4">Title</th>
              <th className="px-6 py-4">Artist</th>
              <th className="px-6 py-4">Published</th>
              <th className="px-6 py-4">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/50">
            {filtered.length === 0 && (
              <tr><td colSpan={4} className="px-6 py-10 text-center text-zinc-600 text-sm font-bold">No drops found</td></tr>
            )}
            {filtered.map(d => (
              <tr key={d.id} className="hover:bg-zinc-800/30 transition-colors">
                <td className="px-6 py-4">
                  <p className="font-bold text-sm text-white">{d.title}</p>
                  <p className="text-[11px] text-zinc-600 font-mono">{d.id?.slice(0,8)}…</p>
                </td>
                <td className="px-6 py-4 text-sm text-zinc-400">{d.artist_name || '—'}</td>
                <td className="px-6 py-4">
                  <span className={`text-[10px] font-black uppercase tracking-widest ${
                    d.is_published ? 'text-emerald-400' : 'text-zinc-600'
                  }`}>{d.is_published ? 'Published' : 'Draft'}</span>
                </td>
                <td className="px-6 py-4 text-[11px] text-zinc-600 font-mono">
                  {d.created_at ? new Date(d.created_at).toLocaleDateString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const LabelAdminDashboard = () => {
  const { profile, profileLoading } = useAuth();
  const [tab,     setTab]     = useState('overview');
  const [artists, setArtists] = useState([]);
  const [drops,   setDrops]   = useState([]);
  const [stats,   setStats]   = useState({ artists: 0, drops: 0, revenue: '$—', subs: 0 });
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // SECURITY FIX #6: Removed console.log(profile) — was leaking PII
  useEffect(() => {
    if (profile?.label_id) fetchAll();
  }, [profile?.label_id]);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      // SECURITY FIX #5: Select only needed columns — not select('*')
      const { data: artistData, error: artistErr } = await supabase
        .from('d2f_artists')
        .select('id,name,slug,genre,avatar_url,access_status')
        .eq('label_id', profile.label_id);

      if (artistErr) throw artistErr;

      const artistIds = (artistData || []).map(a => a.id);
      let dropData = [];
      if (artistIds.length > 0) {
        const { data, error: dropErr } = await supabase
          .from('d2f_drops')
          .select('id,title,is_published,created_at,artist_id')
          .in('artist_id', artistIds)
          .order('created_at', { ascending: false });

        if (dropErr) throw dropErr;
        dropData = data || [];
      }

      const artistMap = Object.fromEntries((artistData || []).map(a => [a.id, a.name]));
      const enriched  = dropData.map(d => ({ ...d, artist_name: artistMap[d.artist_id] }));

      setArtists(artistData || []);
      setDrops(enriched);
      setStats({
        artists: (artistData || []).length,
        drops:   enriched.length,
        revenue: '$—',
        subs:    0,
      });
    } catch (e) {
      // SECURITY FIX #6: Use sanitised logger, not console.error
      logger.error('[LabelAdmin] fetchAll', e);
      setError('Failed to load dashboard data. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  if (profileLoading) return <LoadingScreen message="Loading profile…" />;

  if (!profile?.label_id) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-black text-white">Access Denied</h1>
          <p className="text-zinc-500">You don't have access to the label dashboard.</p>
          {/* SECURITY FIX #3: Removed role/label_id/profile.id from DOM — internal data leakage */}
        </div>
      </div>
    );
  }

  if (loading) return <LoadingScreen message="Loading label dashboard…" />;
  if (error)   return <LoadingScreen message={error} />;

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 pt-32 pb-20 space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-[10px] font-black uppercase tracking-widest">
                Label Admin
              </div>
            </div>
            <h1 className="text-5xl font-black uppercase tracking-tighter">Label HQ</h1>
            <p className="text-zinc-500 mt-1 font-mono text-sm">Manage your roster and releases</p>
          </div>
          <button onClick={fetchAll} className="flex items-center gap-2 px-5 py-3 bg-zinc-900 border border-zinc-800 rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-white hover:border-brand-pink/30 transition-all">
            <SafeIcon icon={FiRefreshCw} /> Refresh
          </button>
        </div>

        <div className="flex gap-2 flex-wrap">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${
                tab === t.id
                  ? 'bg-white text-black shadow-[0_0_15px_rgba(255,255,255,0.15)]'
                  : 'bg-zinc-900 border border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-700'
              }`}>
              <SafeIcon icon={t.icon} className="text-sm" /> {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && <OverviewTab stats={stats} />}
        {tab === 'artists'  && <ArtistsTab artists={artists} />}
        {tab === 'products' && <ProductsTab drops={drops} />}
        {tab === 'orders'   && (
          <div className="p-12 bg-zinc-900 border border-zinc-800 rounded-[2rem] text-center text-zinc-600 font-bold">
            Order history — connect orders table to view revenue
          </div>
        )}
      </div>
    </div>
  );
};

export default LabelAdminDashboard;